import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle, ImageOff, Plus, RefreshCw, Search } from 'lucide-react';
import { PageHeader } from '@/components/layout/page-header';
import { Badge } from '@/components/ui/badge';
import { BusyOverlay } from '@/components/ui/busy-overlay';
import { Button } from '@/components/ui/button';
import { FilterBar, type FilterField } from '@/components/ui/filter-bar';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatDateTime } from '@/lib/format';
import { cn } from '@/lib/utils';
import { BannerAdEditor } from './banner-ad-editor';
import { useBannerAds } from './banner-ads.api';
import {
  BANNER_ACTION,
  PLACEMENT,
  emptyBanner,
  statusOf,
  toInput,
  type BannerAd,
  type BannerAdInput,
  type BannerStatus,
} from './types';

/**
 * Banner ads — the promotional strips the app and web front carry.
 *
 * The list leads with approval, because that is the switch that decides whether
 * a banner runs at all and the one most often left off: a banner can be inside
 * its window, pointed at the right store, and still show nowhere. The original
 * printed `approved` as a bare true/false column beside a separate "active"
 * one and left the reader to work out how they combined.
 */

const ANY = 'ANY';

const STATUS_TONE: Record<
  BannerStatus,
  'success' | 'secondary' | 'outline' | 'warning'
> = {
  live: 'success',
  scheduled: 'secondary',
  ended: 'outline',
  unapproved: 'warning',
};

/** `PLACEMENT.bottom` → `bannerAds.placementBottom`, in both directions. */
const placementLabelKey = (key: string) =>
  `bannerAds.placement${key[0].toUpperCase()}${key.slice(1)}`;

const PLACEMENT_KEY = Object.fromEntries(
  Object.entries(PLACEMENT).map(([key, value]) => [value, key])
) as Record<number, string>;

export function BannerAdsPage() {
  const { t, i18n } = useTranslation();
  const isEnglish = (i18n.resolvedLanguage ?? 'en') === 'en';

  const [draft, setDraft] = useState('');
  const [status, setStatus] = useState(ANY);
  const [placement, setPlacement] = useState(ANY);
  const [editing, setEditing] = useState<{
    id: string | null;
    values: BannerAdInput;
  } | null>(null);

  const list = useBannerAds();
  const banners = useMemo(() => list.data ?? [], [list.data]);

  const counts = useMemo(() => {
    const tally: Record<BannerStatus, number> = {
      live: 0,
      scheduled: 0,
      ended: 0,
      unapproved: 0,
    };
    for (const banner of banners) tally[statusOf(banner)] += 1;
    return tally;
  }, [banners]);

  const term = draft.trim().toLowerCase();
  const rows = useMemo(
    () =>
      banners.filter((banner) => {
        if (status !== ANY && statusOf(banner) !== status) return false;
        if (placement !== ANY && String(banner.placement) !== placement) {
          return false;
        }
        if (!term) return true;
        return [banner.name, banner.businessName]
          .filter(Boolean)
          .some((value) => value!.toLowerCase().includes(term));
      }),
    [banners, status, placement, term]
  );

  const preview = (banner: BannerAd) =>
    (isEnglish ? banner.imageUrl : banner.imageUrlSpa) ||
    banner.imageUrl ||
    banner.imageUrlSpa;

  const fields: FilterField[] = [
    {
      id: 'status',
      label: t('bannerAds.status'),
      value: status,
      onChange: setStatus,
      sort: false,
      resetValue: ANY,
      options: [
        { value: ANY, label: t('bannerAds.statusAny') },
        { value: 'live', label: `${t('bannerAds.statusLive')} (${counts.live})` },
        {
          value: 'scheduled',
          label: `${t('bannerAds.statusScheduled')} (${counts.scheduled})`,
        },
        {
          value: 'unapproved',
          label: `${t('bannerAds.statusUnapproved')} (${counts.unapproved})`,
        },
        {
          value: 'ended',
          label: `${t('bannerAds.statusEnded')} (${counts.ended})`,
        },
      ],
    },
    {
      id: 'placement',
      label: t('bannerAds.position'),
      value: placement,
      onChange: setPlacement,
      sort: false,
      resetValue: ANY,
      options: [
        { value: ANY, label: t('bannerAds.anyPosition') },
        ...Object.entries(PLACEMENT).map(([key, value]) => ({
          value: String(value),
          label: t(placementLabelKey(key)),
        })),
      ],
    },
  ];

  const openEditor = (banner: BannerAd | null) =>
    setEditing(
      banner
        ? { id: banner.id, values: toInput(banner) }
        : { id: null, values: emptyBanner() }
    );

  const StatusCell = ({ banner }: { banner: BannerAd }) => {
    const state = statusOf(banner);
    return (
      <Badge variant={STATUS_TONE[state]}>
        {t(`bannerAds.status${state[0].toUpperCase()}${state.slice(1)}`)}
      </Badge>
    );
  };

  const Thumb = ({ banner }: { banner: BannerAd }) => {
    const src = preview(banner);
    return src ? (
      <img
        src={src}
        alt=""
        // Banners are wide, so the cell is too; cropping one to a square
        // hides the half that carries the message.
        className="bg-muted h-10 w-20 shrink-0 rounded-md object-cover"
        style={
          banner.backgroundColor
            ? { backgroundColor: banner.backgroundColor }
            : undefined
        }
      />
    ) : (
      <div className="bg-muted text-muted-foreground flex h-10 w-20 shrink-0 items-center justify-center rounded-md">
        <ImageOff className="size-4" />
      </div>
    );
  };

  const Where = ({ banner }: { banner: BannerAd }) => (
    <div className="flex flex-wrap gap-1">
      {banner.showInApp && (
        <Badge variant="secondary">{t('bannerAds.app')}</Badge>
      )}
      {banner.showInWeb && (
        <Badge variant="secondary">{t('bannerAds.web')}</Badge>
      )}
      {!banner.showInApp && !banner.showInWeb && (
        <span className="text-muted-foreground text-xs">
          {t('bannerAds.nowhere')}
        </span>
      )}
    </div>
  );

  const actionLabel = (banner: BannerAd) => {
    if (banner.actionType === BANNER_ACTION.none) return t('bannerAds.actionNone');
    const kind =
      banner.actionType === BANNER_ACTION.openLink
        ? t('bannerAds.actionLink')
        : t('bannerAds.actionSearch');
    return banner.actionValue ? `${kind}: ${banner.actionValue}` : kind;
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('bannerAds.title')}
        description={t('bannerAds.subtitle')}
        actions={
          <>
            <Button
              variant="outline"
              size="icon-sm"
              aria-label={t('common.reload')}
              disabled={list.isFetching}
              onClick={() => void list.refetch()}
            >
              <RefreshCw className={list.isFetching ? 'animate-spin' : ''} />
            </Button>
            <Button size="sm" onClick={() => openEditor(null)}>
              <Plus />
              <span className="hidden sm:inline">
                {t('bannerAds.newBanner')}
              </span>
            </Button>
          </>
        }
      />

      <FilterBar
        busy={list.isFetching}
        search={
          <div className="relative flex-1">
            <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2" />
            <Input
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              placeholder={t('bannerAds.searchPlaceholder')}
              aria-label={t('bannerAds.searchPlaceholder')}
              className="h-10 pl-9"
            />
          </div>
        }
        fields={fields}
        summary={
          list.isSuccess ? (
            <p className="text-muted-foreground text-sm" aria-live="polite">
              {t('bannerAds.showingOf', {
                shown: rows.length,
                total: banners.length,
              })}
            </p>
          ) : null
        }
      />

      {list.isError && (
        <div className="border-destructive/30 bg-destructive/8 text-destructive flex items-start gap-2 rounded-xl border p-4 text-sm">
          <AlertCircle className="mt-0.5 size-4 shrink-0" />
          <div className="space-y-2">
            <p>{t('bannerAds.loadFailed')}</p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => void list.refetch()}
            >
              {t('common.retry')}
            </Button>
          </div>
        </div>
      )}

      {list.isPending && (
        <div className="divide-y rounded-xl border">
          {Array.from({ length: 5 }).map((_, index) => (
            <div key={index} className="flex items-center gap-3 p-3">
              <Skeleton className="h-10 w-20 shrink-0 rounded-md" />
              <div className="flex-1 space-y-1.5">
                <Skeleton className="h-4 w-40" />
                <Skeleton className="h-3 w-56" />
              </div>
              <Skeleton className="h-5 w-16" />
            </div>
          ))}
        </div>
      )}

      {list.isSuccess && rows.length === 0 && (
        <div className="relative rounded-xl">
          <BusyOverlay show={list.isFetching} />
          <p className="text-muted-foreground rounded-xl border border-dashed py-14 text-center text-sm">
            {banners.length === 0
              ? t('bannerAds.none')
              : t('bannerAds.noneMatch')}
          </p>
        </div>
      )}

      {rows.length > 0 && (
        <>
          <div className="hidden rounded-xl border lg:block">
            <Table busy={list.isFetching}>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="w-24" />
                  <TableHead>{t('bannerAds.name')}</TableHead>
                  <TableHead>{t('bannerAds.position')}</TableHead>
                  <TableHead>{t('bannerAds.window')}</TableHead>
                  <TableHead>{t('bannerAds.shownIn')}</TableHead>
                  <TableHead>{t('bannerAds.status')}</TableHead>
                  <TableHead className="w-20 text-right" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((banner) => (
                  <TableRow key={banner.id}>
                    <TableCell>
                      <Thumb banner={banner} />
                    </TableCell>
                    <TableCell>
                      <div className="min-w-0">
                        <p className="truncate font-medium">
                          {banner.name || '—'}
                        </p>
                        <p className="text-muted-foreground truncate text-xs">
                          {banner.businessName || t('bannerAds.allStores')}
                        </p>
                        <p className="text-muted-foreground truncate text-xs">
                          {actionLabel(banner)}
                        </p>
                      </div>
                    </TableCell>
                    <TableCell className="whitespace-nowrap">
                      <p>
                        {t(
                          placementLabelKey(
                            PLACEMENT_KEY[banner.placement] ?? 'top'
                          )
                        )}
                      </p>
                      <p className="text-muted-foreground text-xs tabular-nums">
                        #{banner.ordering}
                      </p>
                    </TableCell>
                    <TableCell className="text-xs whitespace-nowrap">
                      <p>{formatDateTime(banner.startDate)}</p>
                      <p className="text-muted-foreground">
                        {formatDateTime(banner.endDate)}
                      </p>
                    </TableCell>
                    <TableCell>
                      <Where banner={banner} />
                    </TableCell>
                    <TableCell>
                      <StatusCell banner={banner} />
                    </TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" onClick={() => openEditor(banner)}>
                        {t('bannerAds.edit')}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <div className="relative lg:hidden">
            <BusyOverlay show={list.isFetching} />
            <div
              className={cn(
                'space-y-2',
                list.isFetching && 'opacity-60 transition-opacity'
              )}
            >
              {rows.map((banner) => (
                <div
                  key={banner.id}
                  className="bg-card space-y-3 rounded-xl border p-3"
                >
                  <div className="flex items-start gap-3">
                    <Thumb banner={banner} />
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-medium">
                        {banner.name || '—'}
                      </p>
                      <p className="text-muted-foreground truncate text-xs">
                        {banner.businessName || t('bannerAds.allStores')}
                      </p>
                    </div>
                    <StatusCell banner={banner} />
                  </div>
                  <p className="text-muted-foreground text-xs">
                    {formatDateTime(banner.startDate)} →{' '}
                    {formatDateTime(banner.endDate)}
                  </p>
                  <Where banner={banner} />
                  <Button
                    size="sm"
                    className="w-full"
                    onClick={() => openEditor(banner)}
                  >
                    {t('bannerAds.edit')}
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      <BannerAdEditor
        banner={editing}
        open={editing !== null}
        onOpenChange={(next) => {
          if (!next) setEditing(null);
        }}
      />
    </div>
  );
}
